/* ─── One date format for the whole analytics surface (spec §11-ts, MS-701 AC13) ───

   Instants are stored and sorted in UTC and displayed in the browser's timezone,
   as `dd Month yyyy hh:mm` on a 24-hour clock. Changing the interface language
   changes the words; it never changes the instant.

   `timeZone` and `locale` are parameters with sensible defaults rather than being
   read from the environment inside, so a test can pin them. A formatter that can
   only be exercised on a UTC machine cannot be used to prove a timezone rule.
   Callers pass `i18n.language`; the `en-GB` default is what you get with no
   interface language at all, not a decision about the viewer.

   **Words localise; numbers do not.** Month names come from the caller's locale,
   but the day and year are read in a fixed locale on purpose. They are parsed
   back into integers here (month arithmetic for quarters, `slice(2)` for the `'yy`
   suffix), and a locale with non-Latin digits or a non-Gregorian calendar — `ar-EG`,
   `th-TH` — would turn "2026" into a string neither operation survives. */

export type BucketGranularity = 'day' | 'month' | 'quarter' | 'year' | string

export interface FormatOptions {
  timeZone?: string
  locale?: string
}

/** The locale used for values that are read as numbers rather than as words. */
const NUMERIC_LOCALE = 'en-GB'

function resolve(options?: FormatOptions) {
  return {
    timeZone: options?.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale: options?.locale ?? NUMERIC_LOCALE,
  }
}

function parse(iso: string): Date | null {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : date
}

/** The calendar fields of an instant *as seen in `timeZone`*, which is what every
 *  label below is built from. Reading `date.getMonth()` instead would label by the
 *  host's zone and put a Berlin day bucket (22:00 UTC) on the previous day. */
function partsIn(date: Date, timeZone: string, locale: string) {
  const formatted = new Intl.DateTimeFormat(locale, {
    timeZone,
    year: 'numeric',
    month: 'long',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date)
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    formatted.find(p => p.type === type)?.value ?? ''
  // `hour12: false` still renders midnight as "24" in some ICU builds.
  const hour = get('hour') === '24' ? '00' : get('hour')
  return {
    day: get('day'),
    month: get('month'),
    year: get('year'),
    hour,
    minute: get('minute'),
  }
}

/** Numeric month (1-12) in `timeZone` — needed for quarter maths, which no
 *  `Intl` option produces directly. */
function monthNumberIn(date: Date, timeZone: string): number {
  const value = new Intl.DateTimeFormat(NUMERIC_LOCALE, { timeZone, month: 'numeric' }).format(date)
  return Number.parseInt(value, 10)
}

/** Day of the month (1-31) in `timeZone`. */
function dayNumberIn(date: Date, timeZone: string): number {
  const value = new Intl.DateTimeFormat(NUMERIC_LOCALE, { timeZone, day: 'numeric' }).format(date)
  return Number.parseInt(value, 10)
}

function shortMonthIn(date: Date, timeZone: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { timeZone, month: 'short' }).format(date)
}

function yearIn(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat(NUMERIC_LOCALE, { timeZone, year: 'numeric' }).format(date)
}

function dayIn(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat(NUMERIC_LOCALE, { timeZone, day: 'numeric' }).format(date)
}

/**
 * Does this bucket start a calendar month *in the viewer's zone*?
 *
 * The daily axis marks the first of each month. Deciding that from the rendered
 * label ("does it start with `1 `?") only works while the label is English and
 * day-first — it silently stops marking anything under `de-DE` ("1. Jan") or a
 * month-first locale, and marks the wrong columns under one that pads the day.
 * The question is about the instant, so it is answered from the instant.
 */
export function isFirstDayOfMonth(iso: string, options?: FormatOptions): boolean {
  const date = parse(iso)
  if (!date) return false
  return dayNumberIn(date, resolve(options).timeZone) === 1
}

/**
 * The daily axis's month marker: the short month, plus a `'yy` suffix in January
 * so the year change is visible on an axis that otherwise never shows one
 * (prototype `agent-studio.html:11991`).
 */
export function formatMonthMarker(iso: string, options?: FormatOptions): string {
  const date = parse(iso)
  if (!date) return ''
  const { timeZone, locale } = resolve(options)
  const month = shortMonthIn(date, timeZone, locale)
  if (monthNumberIn(date, timeZone) !== 1) return month
  return `${month} '${yearIn(date, timeZone).slice(2)}`
}

/** `dd Month yyyy hh:mm`, 24-hour, in the viewer's timezone. Empty string for a
 *  missing or unparseable value — a row with no timestamp renders blank rather
 *  than "Invalid Date". */
export function formatDateTime(iso: string, options?: FormatOptions): string {
  const date = parse(iso)
  if (!date) return ''
  const { timeZone, locale } = resolve(options)
  const p = partsIn(date, timeZone, locale)
  return `${p.day} ${p.month} ${p.year} ${p.hour}:${p.minute}`
}

/** The compact x-axis label for a chart bucket. */
export function formatBucketLabel(
  iso: string,
  granularity: BucketGranularity,
  options?: FormatOptions,
): string {
  const date = parse(iso)
  if (!date) return ''
  const { timeZone, locale } = resolve(options)
  const year = yearIn(date, timeZone)
  switch (granularity) {
    case 'year':
      return year
    case 'quarter':
      return `Q${Math.floor((monthNumberIn(date, timeZone) - 1) / 3) + 1} '${year.slice(2)}`
    case 'month':
      return `${shortMonthIn(date, timeZone, locale)} '${year.slice(2)}`
    default:
      return `${dayIn(date, timeZone)} ${shortMonthIn(date, timeZone, locale)}`
  }
}

/** The fuller label used by the bar hover tooltip, where there is room to spell the
 *  month out and no adjacent column to disambiguate against. */
export function formatBucketFullLabel(
  iso: string,
  granularity: BucketGranularity,
  options?: FormatOptions,
): string {
  const date = parse(iso)
  if (!date) return ''
  const { timeZone, locale } = resolve(options)
  const p = partsIn(date, timeZone, locale)
  switch (granularity) {
    case 'day':
      return `${dayIn(date, timeZone)} ${p.month} ${p.year}`
    case 'month':
      return `${p.month} ${p.year}`
    default:
      // Quarter and year are already unambiguous at a glance.
      return formatBucketLabel(iso, granularity, options)
  }
}
